import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import supportApiService from '../../services/supportApiService';

const SupportTokenRecharge = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [agents, setAgents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredAgents, setFilteredAgents] = useState([]);

  // Modal State
  const [showRechargeModal, setShowRechargeModal] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [selectedTokens, setSelectedTokens] = useState('100');
  const [processing, setProcessing] = useState(false);

  const tokenOptions = [
    { value: '100', label: '100 Tokens', price: 200 },
    { value: '200', label: '200 Tokens', price: 400 },
    { value: '300', label: '300 Tokens', price: 600 },
    { value: '400', label: '400 Tokens', price: 800 },
    { value: '500', label: '500 Tokens', price: 1000 },
    { value: '1000', label: '1000 Tokens', price: 2000 },
  ];

  useEffect(() => {
    loadTokenRechargeData();
  }, []);

  useEffect(() => {
    // Filter agents based on search term
    if (searchTerm.trim() === '') {
      setFilteredAgents(agents);
    } else {
      const filtered = agents.filter(agent => 
        agent.agentId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        agent.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        agent.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        agent.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        agent.phoneNumber?.includes(searchTerm)
      );
      setFilteredAgents(filtered);
    }
  }, [searchTerm, agents]);

  const loadTokenRechargeData = async () => {
    try {
      setLoading(true);
      console.log('🔍 Loading token recharge data...');
      const data = await supportApiService.getTokenRecharge();
      console.log('📊 Token Recharge API Response:', data);
      
      if (data.success) {
        setAgents(data.listOfAgent || []);
        setFilteredAgents(data.listOfAgent || []);
        console.log('✅ Token recharge data loaded successfully:', data.listOfAgent?.length, 'agents');
      } else {
        console.error('❌ Token Recharge API returned error:', data.message);
        toast.error(data.message || 'Failed to load token recharge data');
      }
    } catch (error) {
      console.error('❌ Token recharge load error:', error);
      toast.error('Failed to load token recharge data');
    } finally {
      setLoading(false);
    }
  };

  const handleRechargeClick = (agent) => {
    setSelectedAgent(agent);
    setSelectedTokens('100');
    setShowRechargeModal(true);
  };

  const handleHistoryClick = (agentId) => {
    navigate(`/support/dashboard/token-recharge/token-recharge-history/${agentId}`);
  };

  const handleProcessRecharge = async (e) => {
    e.preventDefault();
    if (!selectedAgent) return;

    try {
      setProcessing(true);
      const amount = (parseInt(selectedTokens) * 2).toString();
      const response = await supportApiService.processTokenRecharge(selectedAgent.agentId, selectedTokens, amount);
      
      if (response.success) {
        toast.success(`Successfully recharged ${selectedTokens} tokens for agent ID ${selectedAgent.agentId}!`);
        setShowRechargeModal(false);
        loadTokenRechargeData();
      } else {
        toast.error(response.message || 'Failed to process token recharge');
      }
    } catch (error) {
      console.error('Error processing token recharge:', error);
      toast.error('Failed to process token recharge');
    } finally {
      setProcessing(false);
    }
  };

  const formatName = (firstName, lastName) => {
    const first = firstName || '';
    const last = lastName || '';
    return `${first} ${last}`.trim() || '-';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-6 max-w-7xl">
        {/* Page Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Token Recharge</h1>
          <p className="text-gray-600">Manage agent token recharges and view token balances</p>
        </div>

        {/* Content Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          {/* Card Header */}
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Tokens Details</h3>
              <div className="text-sm text-gray-500">
                Total: {filteredAgents.length} agents
              </div>
            </div>
          </div>

          {/* Search Bar */}
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="max-w-md">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i className="fas fa-search text-gray-400"></i>
                </div>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-green-500 focus:border-green-500"
                  placeholder="Search by agent ID, name, email..."
                />
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Agent ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Agent Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Mobile Number
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Token
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Recharge
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredAgents.length > 0 ? (
                  filteredAgents.map((agent, index) => (
                    <tr key={agent.agentId || index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {agent.agentId ? (
                          <button
                            onClick={() => handleHistoryClick(agent.agentId)}
                            className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                          >
                            {agent.agentId}
                          </button>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {agent.email || 'NA'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatName(agent.firstName, agent.lastName)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {agent.phoneNumber || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          {agent.tokens || 0} Tokens
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => handleRechargeClick(agent)}
                          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
                        >
                          <i className="fas fa-dollar-sign mr-2"></i>
                          Recharge
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                      {searchTerm ? 'No agents found matching your search.' : 'No agents found.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Footer */}
          {filteredAgents.length > 0 && (
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
              <div className="text-sm text-gray-700">
                Showing {filteredAgents.length} of {agents.length} agents
                {searchTerm && (
                  <span className="ml-2">
                    (filtered by "{searchTerm}")
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Recharge Modal Popup */}
      {showRechargeModal && selectedAgent && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            {/* Background backdrop */}
            <div 
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" 
              onClick={() => !processing && setShowRechargeModal(false)}
            ></div>

            {/* Centered modal box */}
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            
            <div className="inline-block align-middle bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start w-full">
                  <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-bold text-gray-900 mb-4" id="modal-title">
                      Recharge Tokens for Agent
                    </h3>
                    
                    {/* Agent Brief Info */}
                    <div className="bg-blue-50 p-3 rounded-md mb-4 text-sm text-blue-900 space-y-1">
                      <div><span className="font-semibold">Agent ID:</span> {selectedAgent.agentId}</div>
                      <div><span className="font-semibold">Name:</span> {formatName(selectedAgent.firstName, selectedAgent.lastName)}</div>
                      <div><span className="font-semibold">Current Tokens:</span> {selectedAgent.tokens || 0}</div>
                    </div>

                    <form onSubmit={handleProcessRecharge} className="space-y-4">
                      <div>
                        <label htmlFor="tokens" className="block text-sm font-medium text-gray-700 mb-1">
                          Select Token Package
                        </label>
                        <select
                          id="tokens"
                          value={selectedTokens}
                          onChange={(e) => setSelectedTokens(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 text-base"
                          required
                        >
                          {tokenOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label} - ₹{option.price}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label htmlFor="price" className="block text-sm font-medium text-gray-700 mb-1">
                          Total Price (₹2/Token)
                        </label>
                        <input
                          type="text"
                          id="price"
                          value={`₹${parseInt(selectedTokens) * 2}`}
                          readOnly
                          className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-base font-semibold text-green-600"
                        />
                      </div>

                      <div className="bg-gray-50 p-3 rounded-md text-xs text-gray-600 space-y-1">
                        <div className="flex justify-between">
                          <span>Current Balance:</span>
                          <span>{selectedAgent.tokens || 0}</span>
                        </div>
                        <div className="flex justify-between text-green-600 font-medium">
                          <span>Recharging:</span>
                          <span>+{selectedTokens} Tokens</span>
                        </div>
                        <div className="flex justify-between border-t pt-1 font-semibold text-gray-900">
                          <span>Estimated Balance:</span>
                          <span>{((selectedAgent.tokens || 0) + parseInt(selectedTokens))}</span>
                        </div>
                      </div>

                      <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                        <button
                          type="submit"
                          disabled={processing}
                          className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:col-start-2 sm:text-sm disabled:bg-gray-400"
                        >
                          {processing ? 'Processing...' : 'Confirm Recharge'}
                        </button>
                        <button
                          type="button"
                          disabled={processing}
                          onClick={() => setShowRechargeModal(false)}
                          className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:mt-0 sm:col-start-1 sm:text-sm"
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupportTokenRecharge;